import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { toast } from "sonner";
import { formatDateFr } from "../lib/dateFormat";
import {
  Truck, Plus, Search, Filter, RefreshCw, Inbox, Trash2, Undo2,
  Calendar as CalendarIcon, Clock, CheckCircle2, Hourglass, Ban, PenSquare, Download,
  Building2, PackagePlus, PackageX, Boxes,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import TimeInput24 from "./TimeInput24";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import SignaturePad from "./SignaturePad";

const STATUS_BADGE = {
  pending:              { label: "En attente référent",   cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Hourglass },
  awaiting_controller:  { label: "À contre-signer",       cls: "bg-sky-50 text-sky-700 border-sky-200",       Icon: PenSquare },
  confirmed:            { label: "Confirmé",              cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  cancelled:            { label: "Annulé",                cls: "bg-zinc-50 text-zinc-600 border-zinc-200",    Icon: Ban },
};

const TRASH_RETENTION_DAYS = 30;

function daysUntilPurge(deletedAtIso) {
  if (!deletedAtIso) return null;
  const deletedAt = new Date(deletedAtIso);
  const purgeAt = new Date(deletedAt.getTime() + TRASH_RETENTION_DAYS * 24 * 60 * 60 * 1000);
  const diffMs = purgeAt.getTime() - Date.now();
  return Math.max(0, Math.ceil(diffMs / (24 * 60 * 60 * 1000)));
}

const ETAT_LABELS = {
  bon: "Bon état",
  use: "Usé",
  endommage: "Endommagé",
  inutilisable: "Inutilisable",
  perte: "Inutilisable", // legacy
};

const ETATS_SORTIE_DEFAULT = new Set(["endommage", "inutilisable", "perte"]);

const STOCK_ACTION_BADGE = {
  reintegrate: { label: "Stock réintégré", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  remove:      { label: "Sortie définitive", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

/**
 * Vue "Enlèvements sites" — gestion des enlèvements de matériels installés sur sites.
 * Pattern symétrique à "Restitution collaborateurs" (cf. EquipmentReturnsTab.jsx) :
 * 1) admin/manager crée une demande pour un référent collaborateur
 * 2) le référent signe sur site avec état du matériel
 * 3) admin/manager contre-signe → l'affectation site est libérée (status picked_up)
 */
export default function EquipmentSiteReturnsView() {
  const { user } = useAuth();
  const { brands, selectedBrandId } = useBrands();
  const isAdmin = user?.role === "admin";

  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [collabs, setCollabs] = useState([]);
  const [siteAssigns, setSiteAssigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("awaiting_controller");
  const [siteFilter, setSiteFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState("");

  // Nouvelle demande
  const [openNew, setOpenNew] = useState(false);
  const [newForm, setNewForm] = useState({
    pickup_mode: "planned",            // "planned" | "immediate"
    site_id: "",                      // sélection du site EN PREMIER
    site_assignment_id: "",
    // Mode planned :
    contact_collaborateur_id: "",
    return_date: "",
    return_start_time: "09:00",
    return_end_time: "10:00",
    // Mode immediate :
    etat: "",
    etat_note: "",
    controller_signature: null,
    note: "",
  });

  // Suppression
  const [deletingId, setDeletingId] = useState("");

  // Contre-signature
  const [signDialog, setSignDialog] = useState(null);
  const [signForm, setSignForm] = useState({ controller_signature: null, controller_name: "" });
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [submittingReject, setSubmittingReject] = useState(false);

  // Stock action (étape 3)
  const [stockDialog, setStockDialog] = useState(null);
  const [stockForm, setStockForm] = useState({ action: "reintegrate", note: "" });
  const [submittingStock, setSubmittingStock] = useState(false);

  function openStockDialog(r) {
    const defaultAction = ETATS_SORTIE_DEFAULT.has(r.etat) ? "remove" : "reintegrate";
    setStockForm({ action: defaultAction, note: "" });
    setStockDialog(r);
  }

  async function submitStockAction() {
    if (!stockDialog) return;
    setSubmittingStock(true);
    try {
      await api.put(`/equipment-site-returns/${stockDialog.id}/stock-action`, {
        action: stockForm.action,
        note: stockForm.note || null,
      });
      toast.success(
        stockForm.action === "remove"
          ? "Sortie définitive enregistrée — stock mis à jour."
          : "Matériel réintégré au stock."
      );
      setStockDialog(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'action stock.");
    } finally {
      setSubmittingStock(false);
    }
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await api.get("/equipment-site-returns", { params });
      setItems(res.data || []);
    } catch {
      toast.error("Erreur lors du chargement des enlèvements");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const loadRefs = useCallback(async () => {
    try {
      const [sitesRes, collabsRes, assignsRes] = await Promise.all([
        api.get("/sites"),
        api.get("/collaborateurs"),
        api.get("/equipment-site-assignments"),
      ]);
      setSites(sitesRes.data || []);
      setCollabs(collabsRes.data || []);
      // On ne propose que les affectations actuellement sur site (status confirmed)
      setSiteAssigns((assignsRes.data || []).filter((a) => a.status === "confirmed"));
    } catch {
      // silencieux
    }
  }, []);
  useEffect(() => { loadRefs(); }, [loadRefs]);

  useEffect(() => {
    const onNotif = (ev) => {
      const t = ev?.detail?.type || "";
      if (t.startsWith("equipment_site_return_")) load();
    };
    const onUpdated = () => load();
    window.addEventListener("plania:notification", onNotif);
    window.addEventListener("plania:equipment-site-returns-updated", onUpdated);
    const pollId = setInterval(load, 60_000);
    return () => {
      window.removeEventListener("plania:notification", onNotif);
      window.removeEventListener("plania:equipment-site-returns-updated", onUpdated);
      clearInterval(pollId);
    };
  }, [load]);

  // Affectations site éligibles à un enlèvement (status confirmed, pas de demande pending en cours)
  const eligibleSiteAssigns = useMemo(() => {
    const pendingIds = new Set(
      items.filter((i) => i.status === "pending" && !i.deleted_at).map((i) => i.site_assignment_id),
    );
    return siteAssigns.filter((a) => !pendingIds.has(a.id));
  }, [siteAssigns, items]);

  // Sites du département sélectionné AYANT au moins une affectation éligible
  const eligibleSites = useMemo(() => {
    const siteIdsWithAssigns = new Set(eligibleSiteAssigns.map((a) => a.site_id));
    return sites.filter(
      (s) => (!selectedBrandId || s.brand_id === selectedBrandId)
        && siteIdsWithAssigns.has(s.id),
    );
  }, [sites, eligibleSiteAssigns, selectedBrandId]);

  // Affectations du site sélectionné dans la modale uniquement
  const selectableSiteAssigns = useMemo(() => {
    if (!newForm.site_id) return [];
    return eligibleSiteAssigns.filter((a) => a.site_id === newForm.site_id);
  }, [eligibleSiteAssigns, newForm.site_id]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let base = items;
    if (siteFilter && siteFilter !== "all") {
      base = base.filter((r) => r.site_id === siteFilter);
    }
    if (dateFrom) base = base.filter((r) => (r.created_at || "") >= dateFrom);
    if (dateTo) base = base.filter((r) => (r.created_at || "") < dateTo + "T99");
    if (!q) return base;
    return base.filter((r) => (
      (r.site_name_snapshot || "").toLowerCase().includes(q)
      || (r.contact_name || "").toLowerCase().includes(q)
      || (r.designation_snapshot || "").toLowerCase().includes(q)
      || (r.ref_id_snapshot || "").toLowerCase().includes(q)
      || (r.note || "").toLowerCase().includes(q)
      || (r.return_date || "").includes(q)
    ));
  }, [items, search, siteFilter, dateFrom, dateTo]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
      if (siteFilter && siteFilter !== "all") params.set("site_id", siteFilter);
      if (dateFrom) params.set("start_date", dateFrom);
      if (dateTo) {
        const d = new Date(dateTo);
        d.setDate(d.getDate() + 1);
        const pad = (n) => String(n).padStart(2, "0");
        const next = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        params.set("end_date", next);
      }
      const res = await api.get(`/equipment-site-returns/export.csv?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `enlevements_sites_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'export CSV");
    } finally {
      setExporting(false);
    }
  };

  const resetNewForm = () => setNewForm({
    pickup_mode: "planned",
    site_id: "",
    site_assignment_id: "",
    contact_collaborateur_id: "",
    return_date: "",
    return_start_time: "09:00",
    return_end_time: "10:00",
    etat: "",
    etat_note: "",
    controller_signature: null,
    note: "",
  });

  const submitNew = async () => {
    if (!newForm.site_id) return toast.error("Sélectionnez d'abord le site concerné.");
    if (!newForm.site_assignment_id) return toast.error("Sélectionnez le matériel à enlever.");
    if (!newForm.pickup_mode) return toast.error("Sélectionnez le mode d'enlèvement.");

    if (newForm.pickup_mode === "immediate") {
      if (!newForm.etat) return toast.error("Précisez l'état du matériel.");
      if (!newForm.controller_signature) return toast.error("Votre signature est obligatoire pour clôturer l'enlèvement.");
      try {
        await api.post("/equipment-site-returns", {
          site_assignment_id: newForm.site_assignment_id,
          pickup_mode: "immediate",
          etat: newForm.etat,
          etat_note: newForm.etat_note || undefined,
          controller_signature: newForm.controller_signature,
          note: newForm.note || undefined,
        });
        toast.success("Enlèvement effectué et clôturé.");
        setOpenNew(false);
        resetNewForm();
        load();
        window.dispatchEvent(new CustomEvent("plania:equipment-site-returns-updated"));
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erreur");
      }
      return;
    }

    // Mode "planned" — par défaut
    if (!newForm.contact_collaborateur_id) return toast.error("Sélectionnez un référent sur site.");
    if (!newForm.return_date) return toast.error("Indiquez la date d'enlèvement.");
    if (!newForm.return_start_time || !newForm.return_end_time) return toast.error("Précisez le créneau.");
    if (newForm.return_end_time <= newForm.return_start_time) return toast.error("L'heure de fin doit être après l'heure de début.");
    try {
      await api.post("/equipment-site-returns", {
        site_assignment_id: newForm.site_assignment_id,
        pickup_mode: "planned",
        contact_collaborateur_id: newForm.contact_collaborateur_id,
        return_date: newForm.return_date,
        return_start_time: newForm.return_start_time,
        return_end_time: newForm.return_end_time,
        note: newForm.note || undefined,
      });
      toast.success("Demande d'enlèvement envoyée au référent");
      setOpenNew(false);
      resetNewForm();
      load();
      window.dispatchEvent(new CustomEvent("plania:equipment-site-returns-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const cancelItem = async (id) => {
    if (!window.confirm("Annuler cette demande d'enlèvement ?")) return;
    try {
      await api.delete(`/equipment-site-returns/${id}`);
      toast.success("Demande annulée");
      load();
      window.dispatchEvent(new CustomEvent("plania:equipment-site-returns-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const deleteHistory = async (r) => {
    if (!window.confirm(
      `Mettre cette ligne d'historique d'enlèvement dans la corbeille ?\n\n`
      + `• Site : ${r.site_name_snapshot || "—"}\n`
      + `• Matériel : ${r.designation_snapshot || "—"}\n\n`
      + `Restaurable pendant ${TRASH_RETENTION_DAYS} jours.`
    )) return;
    try {
      setDeletingId(r.id);
      await api.delete(`/equipment-site-returns/${r.id}/history`);
      toast.success("Ligne mise dans la corbeille");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setDeletingId("");
    }
  };

  const restoreFromTrash = async (r) => {
    if (!window.confirm(`Restaurer cette ligne depuis la corbeille ?`)) return;
    try {
      setDeletingId(r.id);
      await api.post(`/equipment-site-returns/${r.id}/restore`);
      toast.success("Ligne restaurée");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setDeletingId("");
    }
  };

  const awaitingControllerCount = items.filter((r) => r.status === "awaiting_controller").length;
  const pendingCount = items.filter((r) => r.status === "pending").length;

  const openSignDialog = (r) => {
    setSignForm({
      controller_signature: null,
      controller_name: user?.name || user?.email || "",
    });
    setRejectMode(false);
    setRejectReason("");
    setSignDialog(r);
  };

  const submitControllerConfirm = async () => {
    if (!signDialog) return;
    if (!signForm.controller_signature) return toast.error("Votre signature est obligatoire.");
    try {
      await api.put(`/equipment-site-returns/${signDialog.id}/controller-confirm`, signForm);
      toast.success("Enlèvement contre-signé — validation définitive.");
      setSignDialog(null);
      load();
      window.dispatchEvent(new CustomEvent("plania:equipment-site-returns-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const submitControllerReject = async () => {
    if (!signDialog) return;
    const reason = rejectReason.trim();
    if (!reason) return toast.error("Le motif de refus est obligatoire.");
    if (reason.length < 5) return toast.error("Précisez le motif (au moins 5 caractères).");
    setSubmittingReject(true);
    try {
      await api.put(`/equipment-site-returns/${signDialog.id}/controller-reject`, {
        reason,
        controller_name: signForm.controller_name || undefined,
      });
      toast.success("Enlèvement refusé — le référent a été notifié.");
      setSignDialog(null);
      setRejectMode(false);
      setRejectReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSubmittingReject(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="equipment-site-returns-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="label-uppercase mb-1 inline-flex items-center gap-1.5">
            <Truck size={11} /> Enlèvements
          </p>
          <h2 className="font-display font-semibold text-xl text-zinc-900">
            Enlèvements sites
          </h2>
          <p className="text-xs text-zinc-600 mt-1 font-mono">
            {statusFilter === "trash"
              ? `Corbeille — ${items.length} ligne(s) supprimée(s), restaurables ${TRASH_RETENTION_DAYS} jours`
              : `Demandes émises aux référents site · ${awaitingControllerCount} à contre-signer · ${pendingCount} en attente référent`}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              type="button" variant="outline" size="sm" onClick={load} disabled={loading}
              data-testid="site-return-refresh-btn"
            >
              <RefreshCw size={13} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Rafraîchir
            </Button>
            <Button
              type="button" variant="outline" size="sm" onClick={exportCsv} disabled={exporting}
              data-testid="site-return-export-csv-btn"
              title="Exporter l'historique des enlèvements (filtres pris en compte)"
            >
              <Download size={13} className={`mr-1.5 ${exporting ? "animate-pulse" : ""}`} />
              {exporting ? "Export…" : "Exporter CSV"}
            </Button>
          </div>
          <Button
            type="button" size="sm" onClick={() => setOpenNew(true)}
            data-testid="site-return-new-btn"
            className="w-full sm:w-auto"
          >
            <Plus size={13} className="mr-1.5" /> Demande d&apos;enlèvement
          </Button>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white border border-zinc-200 rounded-sm p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <Label className="label-uppercase mb-1.5 block inline-flex items-center gap-1.5">
            <Filter size={10} /> Statut
          </Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="site-return-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="awaiting_controller">À contre-signer</SelectItem>
              <SelectItem value="pending">En attente (référent)</SelectItem>
              <SelectItem value="confirmed">Confirmés</SelectItem>
              <SelectItem value="cancelled">Annulés</SelectItem>
              <SelectItem value="all">Tous</SelectItem>
              {isAdmin && (
                <SelectItem value="trash" data-testid="site-return-status-trash">
                  Corbeille (admin)
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-uppercase mb-1.5 block inline-flex items-center gap-1.5">
            <Filter size={10} /> Site
          </Label>
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger data-testid="site-return-site-filter">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les sites</SelectItem>
              {sites.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.nom || s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="label-uppercase mb-1.5 block inline-flex items-center gap-1.5">
              <CalendarIcon size={10} /> Du
            </Label>
            <Input
              type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              data-testid="site-return-date-from"
            />
          </div>
          <div>
            <Label className="label-uppercase mb-1.5 block inline-flex items-center gap-1.5">
              <CalendarIcon size={10} /> Au
            </Label>
            <Input
              type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              data-testid="site-return-date-to"
            />
          </div>
        </div>
        <div>
          <Label className="label-uppercase mb-1.5 block inline-flex items-center gap-1.5">
            <Search size={10} /> Recherche
          </Label>
          <Input
            placeholder="Site, référent, matériel…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="site-return-search-input"
          />
        </div>
      </div>

      {/* Liste */}
      <div className="bg-white border border-zinc-200 rounded-sm">
        {loading && filtered.length === 0 ? (
          <p className="text-sm text-zinc-500 font-mono py-10 text-center">Chargement…</p>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center" data-testid="site-return-empty">
            <Inbox className="mx-auto text-zinc-300 mb-3" size={36} />
            <p className="text-sm text-zinc-500 font-mono">
              {statusFilter === "trash"
                ? "La corbeille est vide."
                : `Aucun enlèvement ${statusFilter === "pending" ? "en attente" : ""} pour le moment.`}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {filtered.map((r) => {
              const isTrashed = !!r.deleted_at;
              const badge = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
              const brand = brands.find((b) => b.id === r.brand_id);
              const StatusIcon = badge.Icon;
              const daysLeft = isTrashed ? daysUntilPurge(r.deleted_at) : null;
              return (
                <div
                  key={r.id}
                  className={`p-4 flex items-start gap-3 flex-wrap ${isTrashed ? "bg-zinc-50/60" : ""}`}
                  data-testid={`site-return-row-${r.id}`}
                >
                  <span
                    className="h-12 w-1 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: isTrashed ? "#a1a1aa" : (brand?.color || "#0EA5A4") }}
                  />
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-orange-50 border-orange-200 text-orange-800">
                        <Truck size={10} /> Enlèvement
                      </span>
                      <p className="text-sm font-medium text-zinc-900 inline-flex items-center gap-1">
                        <Building2 size={12} className="text-zinc-400" />
                        {r.site_name_snapshot || "—"}
                      </p>
                      {isTrashed ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-zinc-100 border-zinc-300 text-zinc-700">
                          <Trash2 size={10} /> Corbeille · Statut d&apos;origine&nbsp;:&nbsp;{badge.label}
                        </span>
                      ) : (
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider ${badge.cls}`}>
                          <StatusIcon size={10} /> {badge.label}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-zinc-800 mt-1">
                      <b>{r.designation_snapshot || "—"}</b>
                      {r.ref_id_snapshot ? (
                        <span className="text-xs font-mono text-zinc-500 ml-2">({r.ref_id_snapshot})</span>
                      ) : null}
                      <span className="text-xs font-mono text-zinc-500 ml-2">· Qté {r.quantite_snapshot || 1}</span>
                    </p>
                    <p className="text-xs text-zinc-600 font-mono mt-1">
                      Référent : <b>{r.contact_name || "—"}</b>
                      {" · "}
                      <CalendarIcon size={10} className="inline mr-1" />
                      Enlèvement prévu : <b>{r.return_date ? formatDateFr(r.return_date) : "—"}</b>
                      {" "}<Clock size={10} className="inline mx-1" />
                      {r.return_start_time} → {r.return_end_time}
                      {brand?.name ? <> · {brand.name}</> : null}
                    </p>
                    {r.note && (
                      <p className="text-xs text-zinc-600 mt-1 italic">« {r.note} »</p>
                    )}
                    {(r.status === "awaiting_controller" || r.status === "confirmed") && (
                      <p className="text-xs text-sky-700 font-mono mt-1">
                        Signé par le référent le {r.actual_date ? formatDateFr(r.actual_date) : "—"} à {r.actual_time || "—"} · État déclaré : <b>{ETAT_LABELS[r.etat] || r.etat || "—"}</b>
                        {r.etat_note ? ` — ${r.etat_note}` : ""}
                      </p>
                    )}
                    {r.status === "confirmed" && (
                      <p className="text-xs text-emerald-700 font-mono mt-1">
                        Contre-signé par {r.controller_name || "—"}
                        {r.controller_signed_at ? ` le ${formatDateFr(r.controller_signed_at.slice(0, 10))}` : ""}
                        {" — affectation site libérée."}
                      </p>
                    )}
                    {r.stock_action && STOCK_ACTION_BADGE[r.stock_action] && (
                      <p className="text-xs font-mono mt-1" data-testid={`site-return-stock-state-${r.id}`}>
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider ${STOCK_ACTION_BADGE[r.stock_action].cls}`}>
                          {r.stock_action === "remove" ? <PackageX size={10} /> : <PackagePlus size={10} />}
                          {STOCK_ACTION_BADGE[r.stock_action].label}
                        </span>
                        <span className="text-zinc-500 ml-2">
                          par {r.stock_action_by_name || "—"}
                          {r.stock_action_at ? ` le ${formatDateFr(r.stock_action_at.slice(0, 10))}` : ""}
                        </span>
                        {r.stock_action_note && (
                          <span className="text-zinc-600 italic ml-2">« {r.stock_action_note} »</span>
                        )}
                      </p>
                    )}
                    <p className="text-[11px] text-zinc-400 font-mono mt-1">
                      Émise par {r.requested_by_name || "—"}
                      {r.created_at ? ` le ${formatDateFr(r.created_at.slice(0, 10))}` : ""}
                    </p>
                    {isTrashed && (
                      <p className="text-[11px] text-zinc-600 font-mono mt-1 italic" data-testid={`site-return-trash-meta-${r.id}`}>
                        <Trash2 size={10} className="inline mr-1" />
                        Supprimée par <b>{r.deleted_by_name || "—"}</b>
                        {r.deleted_at ? ` le ${formatDateFr(r.deleted_at.slice(0, 10))}` : ""}
                        {" · Purge automatique dans "}<b>{daysLeft ?? "—"} jour{(daysLeft ?? 0) > 1 ? "s" : ""}</b>
                      </p>
                    )}
                  </div>
                  {!isTrashed && r.status === "awaiting_controller" && (
                    <Button
                      type="button" size="sm"
                      onClick={() => openSignDialog(r)}
                      className="bg-sky-600 hover:bg-sky-700"
                      data-testid={`site-return-controller-sign-${r.id}`}
                    >
                      <PenSquare size={13} className="mr-1.5" /> Contre-signer
                    </Button>
                  )}
                  {!isTrashed && r.status === "confirmed" && !r.stock_action && (user?.role === "admin" || user?.role === "manager") && (
                    <Button
                      type="button" size="sm"
                      onClick={() => openStockDialog(r)}
                      className="bg-teal-600 hover:bg-teal-700"
                      data-testid={`site-return-stock-action-${r.id}`}
                      title="Réintégrer au stock ou sortir définitivement"
                    >
                      <Boxes size={13} className="mr-1.5" /> Action stock
                    </Button>
                  )}
                  {!isTrashed && isAdmin && (r.status === "pending" || r.status === "awaiting_controller") && (
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => cancelItem(r.id)}
                      className="border-red-200 text-red-700 hover:bg-red-50"
                      data-testid={`site-return-cancel-${r.id}`}
                    >
                      <Ban size={13} className="mr-1.5" /> Annuler
                    </Button>
                  )}
                  {!isTrashed && isAdmin && (
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => deleteHistory(r)}
                      disabled={deletingId === r.id}
                      className="h-8 px-2 text-red-700 border-red-200 hover:bg-red-50"
                      data-testid={`site-return-delete-${r.id}`}
                      title="Mettre cette ligne dans la corbeille (restaurable 30 jours)"
                    >
                      <Trash2 size={13} />
                    </Button>
                  )}
                  {isTrashed && isAdmin && (
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => restoreFromTrash(r)}
                      disabled={deletingId === r.id}
                      className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      data-testid={`site-return-restore-${r.id}`}
                      title="Restaurer cette ligne depuis la corbeille"
                    >
                      <Undo2 size={13} className="mr-1.5" /> Restaurer
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modale Nouvelle demande */}
      <Dialog
        open={openNew}
        onOpenChange={(v) => { setOpenNew(v); if (!v) resetNewForm(); }}
      >
        <DialogContent
          className="flex flex-col max-h-[90vh] p-0 sm:max-w-lg"
          data-testid="site-return-new-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
            <DialogTitle>Nouvelle demande d&apos;enlèvement</DialogTitle>
            <DialogDescription className="sr-only">
              Émettre une demande d&apos;enlèvement à un référent collaborateur pour un matériel installé sur un site.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto px-6 py-4 flex-1 space-y-4">
            <div>
              <Label className="label-uppercase mb-1.5 block">
                Site concerné <span className="text-red-600">*</span>
                <span className="text-[10px] text-zinc-400 ml-2 normal-case">
                  (sites du département sélectionné ayant du matériel installé)
                </span>
              </Label>
              {eligibleSites.length === 0 ? (
                <p className="text-xs text-amber-700 font-mono">
                  Aucun site du département sélectionné n&apos;a actuellement de matériel à enlever.
                </p>
              ) : (
                <Select
                  value={newForm.site_id}
                  onValueChange={(v) => setNewForm({ ...newForm, site_id: v, site_assignment_id: "" })}
                >
                  <SelectTrigger data-testid="site-return-site-select">
                    <SelectValue placeholder="Sélectionner le site…" />
                  </SelectTrigger>
                  <SelectContent>
                    {eligibleSites.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.nom || s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div>
              <Label className="label-uppercase mb-1.5 block">
                Matériel à enlever <span className="text-red-600">*</span>
                <span className="text-[10px] text-zinc-400 ml-2 normal-case">
                  (matériels actuellement installés sur le site sélectionné)
                </span>
              </Label>
              {!newForm.site_id ? (
                <p className="text-xs text-zinc-500 font-mono italic">
                  Sélectionnez d&apos;abord un site.
                </p>
              ) : selectableSiteAssigns.length === 0 ? (
                <p className="text-xs text-amber-700 font-mono">
                  Aucun matériel disponible à enlever pour ce site (déjà en demande ou retiré).
                </p>
              ) : (
                <Select
                  value={newForm.site_assignment_id}
                  onValueChange={(v) => setNewForm({ ...newForm, site_assignment_id: v })}
                >
                  <SelectTrigger data-testid="site-return-assignment-select">
                    <SelectValue placeholder="Sélectionner le matériel…" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectableSiteAssigns.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.designation_snapshot || "—"}
                        {a.ref_id_snapshot ? ` (${a.ref_id_snapshot})` : ""}
                        {a.quantite_sortie ? ` — Qté ${a.quantite_sortie}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Mode d'enlèvement — radio */}
            <div>
              <Label className="label-uppercase mb-1.5 block">
                Mode d&apos;enlèvement <span className="text-red-600">*</span>
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="pickup-mode-immediate"
                  onClick={() => setNewForm({ ...newForm, pickup_mode: "immediate" })}
                  className={`text-left p-3 rounded-lg border transition-all ${
                    newForm.pickup_mode === "immediate"
                      ? "border-amber-500 bg-amber-50 ring-2 ring-amber-200"
                      : "border-zinc-200 hover:border-zinc-300 bg-white"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Truck className="h-4 w-4 text-amber-700" />
                    <span className="text-sm font-semibold text-zinc-900">Enlèvement immédiat</span>
                  </div>
                  <p className="text-[11px] text-zinc-600 leading-snug">
                    Vous êtes sur place : vous signez et clôturez l&apos;enlèvement maintenant.
                  </p>
                </button>
                <button
                  type="button"
                  data-testid="pickup-mode-planned"
                  onClick={() => setNewForm({ ...newForm, pickup_mode: "planned" })}
                  className={`text-left p-3 rounded-lg border transition-all ${
                    newForm.pickup_mode === "planned"
                      ? "border-sky-500 bg-sky-50 ring-2 ring-sky-200"
                      : "border-zinc-200 hover:border-zinc-300 bg-white"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <CalendarIcon className="h-4 w-4 text-sky-700" />
                    <span className="text-sm font-semibold text-zinc-900">Enlèvement à planifier</span>
                  </div>
                  <p className="text-[11px] text-zinc-600 leading-snug">
                    Désignez un collaborateur référent qui signera sur site à la date prévue.
                  </p>
                </button>
              </div>
            </div>

            {/* Champs Mode "immediate" */}
            {newForm.pickup_mode === "immediate" && (
              <>
                <div>
                  <Label className="label-uppercase mb-1.5 block">
                    État du matériel constaté <span className="text-red-600">*</span>
                  </Label>
                  <Select
                    value={newForm.etat}
                    onValueChange={(v) => setNewForm({ ...newForm, etat: v })}
                  >
                    <SelectTrigger data-testid="site-return-etat-select">
                      <SelectValue placeholder="Sélectionner l&apos;état…" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ETAT_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">
                    Note sur l&apos;état <span className="text-zinc-400 normal-case">(optionnel)</span>
                  </Label>
                  <Textarea
                    rows={2}
                    value={newForm.etat_note}
                    onChange={(e) => setNewForm({ ...newForm, etat_note: e.target.value })}
                    placeholder="Précisions sur l&apos;état observé…"
                    data-testid="site-return-etat-note-input"
                  />
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">
                    Votre signature <span className="text-red-600">*</span>
                  </Label>
                  <SignaturePad
                    value={newForm.controller_signature}
                    onChange={(sig) => setNewForm({ ...newForm, controller_signature: sig })}
                    data-testid="site-return-immediate-signature"
                  />
                  <p className="text-[10px] text-zinc-500 mt-1">
                    Date et heure d&apos;enlèvement automatiquement enregistrées : {formatDateFr(new Date().toISOString())}.
                  </p>
                </div>
              </>
            )}

            {/* Champs Mode "planned" */}
            {newForm.pickup_mode === "planned" && (
              <>
                <div>
                  <Label className="label-uppercase mb-1.5 block">
                    Référent sur site <span className="text-red-600">*</span>
                    <span className="text-[10px] text-zinc-400 ml-2 normal-case">
                      (signera lors de l&apos;enlèvement)
                    </span>
                  </Label>
                  <Select
                    value={newForm.contact_collaborateur_id}
                    onValueChange={(v) => setNewForm({ ...newForm, contact_collaborateur_id: v })}
                  >
                    <SelectTrigger data-testid="site-return-contact-select">
                      <SelectValue placeholder="Sélectionner un référent…" />
                    </SelectTrigger>
                    <SelectContent>
                      {collabs.filter((c) => c.is_active !== false).map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} — {c.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-3 sm:col-span-1">
                    <Label className="label-uppercase mb-1.5 block">
                      Date <span className="text-red-600">*</span>
                    </Label>
                    <Input
                      type="date"
                      value={newForm.return_date}
                      onChange={(e) => setNewForm({ ...newForm, return_date: e.target.value })}
                      data-testid="site-return-date-input"
                    />
                  </div>
                  <div className="col-span-3 sm:col-span-1">
                    <Label className="label-uppercase mb-1.5 block">
                      Début <span className="text-red-600">*</span>
                    </Label>
                    <TimeInput24
                      value={newForm.return_start_time}
                      onChange={(v) => setNewForm({ ...newForm, return_start_time: v })}
                      data-testid="site-return-start-input"
                    />
                  </div>
                  <div className="col-span-3 sm:col-span-1">
                    <Label className="label-uppercase mb-1.5 block">
                      Fin <span className="text-red-600">*</span>
                    </Label>
                    <TimeInput24
                      value={newForm.return_end_time}
                      onChange={(v) => setNewForm({ ...newForm, return_end_time: v })}
                      data-testid="site-return-end-input"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <Label className="label-uppercase mb-1.5 block">
                Note <span className="text-zinc-400 normal-case">(optionnel)</span>
              </Label>
              <Textarea
                rows={3}
                value={newForm.note}
                onChange={(e) => setNewForm({ ...newForm, note: e.target.value })}
                placeholder="Motif, lieu précis, précisions…"
                data-testid="site-return-note-input"
              />
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
            <Button variant="outline" onClick={() => setOpenNew(false)}>Annuler</Button>
            <Button onClick={submitNew} data-testid="site-return-new-submit">
              {newForm.pickup_mode === "immediate"
                ? "Signer et clôturer l'enlèvement"
                : "Envoyer la demande"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale Contre-signature */}
      <Dialog
        open={!!signDialog}
        onOpenChange={(v) => { if (!v) setSignDialog(null); }}
      >
        <DialogContent
          className="flex flex-col max-h-[90vh] p-0 sm:max-w-lg"
          data-testid="site-return-controller-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
            <DialogTitle>
              {rejectMode ? "Refuser l'enlèvement" : "Contre-signer l'enlèvement"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {rejectMode
                ? "Indiquez le motif du refus. Le référent recevra une notification et devra re-confirmer."
                : "Le référent a signé sur site. Confirmez par votre contre-signature."}
            </DialogDescription>
          </DialogHeader>
          {signDialog && (
            <div className="overflow-y-auto px-6 py-4 flex-1 space-y-4">
              <div className={`${rejectMode ? "bg-red-50 border-red-200" : "bg-sky-50 border-sky-200"} border rounded-sm p-3 text-sm space-y-1`}>
                <p>
                  <b>{signDialog.designation_snapshot || "—"}</b>
                  <span className="text-xs font-mono text-zinc-600 ml-2">({signDialog.ref_id_snapshot || "—"})</span>
                  <span className="text-xs font-mono text-zinc-500 ml-2">· Qté {signDialog.quantite_snapshot || 1}</span>
                </p>
                <p className="text-xs font-mono text-zinc-700">
                  Site : <b>{signDialog.site_name_snapshot || "—"}</b>
                </p>
                <p className="text-xs font-mono text-zinc-700">
                  Référent : <b>{signDialog.contact_name || "—"}</b>
                </p>
                <p className="text-xs font-mono text-zinc-700">
                  Signé le <b>{signDialog.actual_date ? formatDateFr(signDialog.actual_date) : "—"}</b>
                  {signDialog.actual_time ? <> à <b>{signDialog.actual_time}</b></> : null}
                  {" · État déclaré : "}<b>{ETAT_LABELS[signDialog.etat] || signDialog.etat || "—"}</b>
                </p>
                {signDialog.etat_note && (
                  <p className="text-xs text-zinc-600 italic">« {signDialog.etat_note} »</p>
                )}
                {signDialog.rejection_count > 0 && (
                  <p className="text-[11px] font-mono text-amber-700 pt-1 border-t border-zinc-200 mt-2">
                    Cet enlèvement a déjà été refusé {signDialog.rejection_count} fois.
                  </p>
                )}
              </div>

              {!rejectMode && signDialog.contact_signature && (
                <div>
                  <Label className="label-uppercase mb-1.5 block">Signature du référent</Label>
                  <div className="border border-zinc-200 rounded-sm bg-white p-2 inline-block">
                    <img
                      src={signDialog.contact_signature}
                      alt="Signature référent"
                      className="max-h-24"
                      data-testid="site-return-contact-signature-preview"
                    />
                  </div>
                </div>
              )}

              {!rejectMode ? (
                <>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">
                      Votre nom (contrôleur)
                    </Label>
                    <Input
                      value={signForm.controller_name}
                      onChange={(e) => setSignForm({ ...signForm, controller_name: e.target.value })}
                      placeholder="Nom & prénom"
                      data-testid="site-return-controller-name-input"
                    />
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">
                      Votre signature <span className="text-red-600">*</span>
                    </Label>
                    <SignaturePad
                      value={signForm.controller_signature}
                      onSave={(url) => setSignForm((f) => ({ ...f, controller_signature: url }))}
                    />
                    <p className="text-[11px] text-zinc-500 font-mono mt-1">
                      Votre contre-signature valide définitivement l&apos;enlèvement : l&apos;affectation site sera libérée et le matériel redeviendra disponible en stock.
                    </p>
                  </div>
                </>
              ) : (
                <div>
                  <Label className="label-uppercase mb-1.5 block">
                    Motif du refus <span className="text-red-600">*</span>
                  </Label>
                  <Textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Ex. : l'état déclaré ne correspond pas — matériel rayé. Merci de re-déclarer 'endommagé' avec description."
                    rows={5}
                    data-testid="site-return-reject-reason"
                    className="resize-none"
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white flex-wrap gap-2">
            {!rejectMode ? (
              <>
                <Button variant="outline" onClick={() => setSignDialog(null)}>Annuler</Button>
                <Button
                  variant="outline"
                  onClick={() => setRejectMode(true)}
                  className="border-red-200 text-red-700 hover:bg-red-50"
                  data-testid="site-return-reject-toggle"
                >
                  <Ban size={13} className="mr-1.5" /> Refuser
                </Button>
                <Button
                  onClick={submitControllerConfirm}
                  disabled={!signForm.controller_signature}
                  className="bg-sky-600 hover:bg-sky-700"
                  data-testid="site-return-controller-submit"
                >
                  <CheckCircle2 size={13} className="mr-1.5" /> Valider la contre-signature
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => { setRejectMode(false); setRejectReason(""); }}
                  data-testid="site-return-reject-cancel"
                >
                  Retour
                </Button>
                <Button
                  onClick={submitControllerReject}
                  disabled={submittingReject || rejectReason.trim().length < 5}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="site-return-reject-submit"
                >
                  <Ban size={13} className="mr-1.5" /> Confirmer le refus
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ----- Modale : Action stock ----- */}
      <Dialog open={!!stockDialog} onOpenChange={(o) => !o && setStockDialog(null)}>
        <DialogContent className="max-w-md" data-testid="site-return-stock-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Boxes size={18} className="text-teal-700" /> Action stock
            </DialogTitle>
            <DialogDescription className="text-xs">
              Décidez du sort du matériel après la contre-signature. Cette action est
              <b> irréversible</b>.
            </DialogDescription>
          </DialogHeader>
          {stockDialog && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border bg-zinc-50 p-3">
                <p className="font-medium text-zinc-900">{stockDialog.designation_snapshot || "—"}</p>
                <p className="text-xs text-zinc-600 font-mono mt-0.5">
                  Qté : <b>{stockDialog.quantite_snapshot || 1}</b>
                  {" · "}État déclaré : <b>{ETAT_LABELS[stockDialog.etat] || stockDialog.etat || "—"}</b>
                  {stockDialog.site_name_snapshot && (<> · Site : <b>{stockDialog.site_name_snapshot}</b></>)}
                </p>
              </div>
              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer p-2 rounded border hover:bg-emerald-50" data-testid="site-stock-action-reintegrate-radio">
                  <input
                    type="radio" name="site_stock_action" value="reintegrate"
                    checked={stockForm.action === "reintegrate"}
                    onChange={() => setStockForm((f) => ({ ...f, action: "reintegrate" }))}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="inline-flex items-center gap-1 font-medium text-emerald-700">
                      <PackagePlus size={14} /> Réintégrer au stock
                    </span>
                    <span className="block text-xs text-zinc-600">
                      Matériel utilisable (Bon état ou Usé) — remis au stock disponible.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer p-2 rounded border hover:bg-rose-50" data-testid="site-stock-action-remove-radio">
                  <input
                    type="radio" name="site_stock_action" value="remove"
                    checked={stockForm.action === "remove"}
                    onChange={() => setStockForm((f) => ({ ...f, action: "remove" }))}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="inline-flex items-center gap-1 font-medium text-rose-700">
                      <PackageX size={14} /> Sortie définitive du stock
                    </span>
                    <span className="block text-xs text-zinc-600">
                      Matériel inutilisable (Endommagé / Inutilisable) — décrémente la quantité totale.
                    </span>
                  </span>
                </label>
              </div>
              <div>
                <Label className="text-xs">Note / motif (optionnel)</Label>
                <Textarea
                  value={stockForm.note}
                  onChange={(e) => setStockForm((f) => ({ ...f, note: e.target.value }))}
                  placeholder="Ex : matériel cassé, jeté en DEEE…"
                  rows={2}
                  data-testid="site-stock-action-note-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setStockDialog(null)} disabled={submittingStock}>
              Annuler
            </Button>
            <Button
              type="button"
              onClick={submitStockAction}
              disabled={submittingStock}
              className={stockForm.action === "remove" ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}
              data-testid="site-stock-action-confirm-btn"
            >
              {submittingStock ? "Enregistrement…" : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
